import { useFormContext, useWatch } from 'react-hook-form';
import { ui } from '@procuros/datachecks';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { useNumberConfigurations } from 'components/DataTable/cells/WebEDI/cellHelpers';
import { DisplayModes } from 'components/DataTable/Types';
import { DataDisplay } from 'components/Form/DataDisplay/DataDisplay';
import { SchemableFormInput } from 'components/Form/SchemableFormInput/SchemableFormInput';
import { useFieldState } from 'components/SchemableForm/hooks/useFieldState';
import { Translate } from 'components/Translate/Translate';

type NumberFieldProps = {
  field: ui.NumberField;
  fieldId: string;
  isReadOnly: boolean;
  mode: DisplayModes;
  variant?: 'large';
};
export const NumberField = ({ field, fieldId, isReadOnly, mode, variant }: NumberFieldProps) => {
  const value = useWatch({ name: fieldId });
  const { register } = useFormContext();
  const { service } = useErrorContext<WebEDIError>();
  const error = service.useErrorById(fieldId);
  const numberOfErrors = service.useNumberOfActiveErrors();
  const setCurrentError = service.setCurrentError;
  const fieldState = useFieldState(fieldId, error);

  const { prefix, suffix, formattedValue } = useNumberConfigurations({
    meta: {
      optionalConfig: 'type_config' in field ? field.type_config : undefined,
      type: field.type,
    },
    value,
    mode: DisplayModes.create,
    fieldId,
    rowData: {}, //we don't need it since we don't need to format according to the data in the same row (there's no row in header)
  });

  if (mode === DisplayModes.view) {
    return (
      <DataDisplay
        value={formattedValue}
        label={field.label}
        totalNumberOfErrors={numberOfErrors}
        error={error}
        fieldId={fieldId}
        onShowFieldErrorModal={() => error && setCurrentError(error)}
        description={field.description}
      />
    );
  }
  return (
    <SchemableFormInput
      type="number"
      label={field.label}
      description={field.description ? <Translate i18nKey={field.description} /> : undefined}
      error={error}
      title={formattedValue}
      leftAddon={prefix}
      rightAddon={suffix}
      totalNumberOfErrors={numberOfErrors}
      {...register(fieldId, { valueAsNumber: true })}
      value={value}
      onShowFieldErrorModal={() => error && setCurrentError(error)}
      required={field.modality === 'MANDATORY'}
      disabled={isReadOnly}
      min={0}
      step={0.01}
      variant={variant}
      valueDescription={field.value_description}
      fieldState={fieldState}
    />
  );
};
